import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import FaqAccordion from '../components/FaqAccordion';
import SectionHeading from '../components/SectionHeading';
import Reveal from '../components/Reveal';
import ArrowLink from '../components/ArrowLink';
import { FAQS, SAMPLE_CONTENT_PLAN, PHOTO_GUIDE } from '../data/content';
import { CalendarDays, Download, Check, Camera } from 'lucide-react';

export default function FaqsPage() {
  return (
    <>
      <Seo
        title="FAQs — Straight Answers for Tradespeople | Build My Trade"
        description="Honest answers about websites, pricing, hosting, ownership, Google verification and monthly support for UK tradespeople."
        path="/faqs"
      />
      <PageHero
        eyebrow="FAQs"
        title="Fair questions, straight answers"
        intro="Everything tradespeople usually ask before getting online — answered honestly, including the things we can’t promise."
        crumbs={[{ label: 'FAQs' }]}
        testid="faqs-heading"
      />

      <section className="bg-offwhite" data-testid="faqs-list">
        <div className="mx-auto max-w-5xl px-4 py-16 sm:px-6 lg:px-8 lg:py-24">
          <div className="space-y-14">
            {FAQS.map((group, gi) => (
              <div key={group.category}>
                <Reveal>
                  <h2 className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                    {group.category}
                  </h2>
                </Reveal>
                <Reveal delay={0.08} className="mt-5">
                  <FaqAccordion items={group.items} testid={`faq-group-${gi}`} />
                </Reveal>
              </div>
            ))}
          </div>

          {/* Sample content plan */}
          <Reveal>
            <section className="mt-16" data-testid="content-planner" aria-labelledby="content-planner-heading">
              <div className="overflow-hidden rounded-2xl bg-charcoal">
                <div className="flex flex-col gap-6 border-b border-charcoal-line p-8 sm:flex-row sm:items-center sm:justify-between sm:p-10">
                  <div>
                    <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-lime">
                      What a monthly plan looks like
                    </p>
                    <h2
                      id="content-planner-heading"
                      className="mt-3 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-offwhite sm:text-3xl"
                    >
                      A sample month of content, on us
                    </h2>
                    <p className="mt-3 max-w-xl text-sm leading-relaxed text-offwhite/65">
                      Wondering what “eight branded posts a month” actually means? Here’s a real example month from
                      the Stay Visible plan — the same shape we’d tailor to your trade, your photos and your diary.
                    </p>
                  </div>
                  <a
                    href="/downloads/sample-content-plan.txt"
                    download="build-my-trade-sample-content-plan.txt"
                    data-testid="content-plan-download"
                    className="group inline-flex shrink-0 items-center gap-2 rounded-full bg-lime px-6 py-3 font-heading text-sm font-bold text-charcoal transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-lime-hover"
                  >
                    <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
                    Download this plan
                  </a>
                </div>

                <div className="grid gap-px bg-charcoal-line sm:grid-cols-2 lg:grid-cols-4">
                  {SAMPLE_CONTENT_PLAN.weeks.map((week) => (
                    <div key={week.week} className="bg-charcoal p-6 sm:p-7">
                      <p className="flex items-center gap-2 font-mono text-xs font-semibold uppercase tracking-[0.2em] text-offwhite/50">
                        <CalendarDays className="h-3.5 w-3.5 text-lime" aria-hidden="true" />
                        {week.week}
                      </p>
                      <div className="mt-5 space-y-4">
                        {week.posts.map((post, i) => (
                          <div key={i} className="rounded-xl border border-charcoal-line bg-charcoal-surface p-4">
                            <p className="flex items-center justify-between gap-2">
                              <span className="font-heading text-sm font-bold text-offwhite">{post.type}</span>
                              <span className="rounded-full bg-lime px-2.5 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-wider text-charcoal">
                                {post.day}
                              </span>
                            </p>
                            <p className="mt-2 text-xs leading-relaxed text-offwhite/60">{post.idea}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="border-t border-charcoal-line p-8 sm:p-10">
                  <ul className="grid gap-3 sm:grid-cols-3">
                    {SAMPLE_CONTENT_PLAN.notes.map((note, i) => (
                      <li key={i} className="flex gap-3 text-xs leading-relaxed text-offwhite/65">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-lime" aria-hidden="true" />
                        {note}
                      </li>
                    ))}
                  </ul>
                  <p className="mt-6 text-xs text-offwhite/40">
                    Sample only — your plan is agreed with you each month before anything is posted. Post types vary
                    with the season and your work.
                  </p>
                </div>
              </div>
            </section>
          </Reveal>

          {/* Photo guide — companion to the planner */}
          <Reveal>
            <section className="mt-8" data-testid="photo-guide" aria-labelledby="photo-guide-heading">
              <div className="grid gap-px overflow-hidden rounded-2xl border border-hairline bg-hairline lg:grid-cols-5">
                <div className="bg-white p-8 sm:p-10 lg:col-span-2">
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-stonebrand">
                    Free companion guide
                  </p>
                  <h2
                    id="photo-guide-heading"
                    className="mt-3 text-balance font-heading text-2xl font-bold leading-snug tracking-tight text-charcoal sm:text-3xl"
                  >
                    {PHOTO_GUIDE.title}
                  </h2>
                  <p className="mt-3 text-sm leading-relaxed text-stonebrand">
                    Great websites are built on real job photos — and your phone is all you need. Six habits that
                    take seconds and make your work look as good online as it does in person.
                  </p>
                  <a
                    href="/downloads/job-photo-guide.txt"
                    download="build-my-trade-job-photo-guide.txt"
                    data-testid="photo-guide-download"
                    className="group mt-7 inline-flex items-center gap-2 rounded-full bg-charcoal px-6 py-3 font-heading text-sm font-bold text-offwhite transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-charcoal-surface"
                  >
                    <Download className="h-4 w-4 text-lime transition-transform duration-300 group-hover:translate-y-0.5" aria-hidden="true" />
                    Download the guide
                  </a>
                </div>
                <div className="grid gap-px bg-hairline sm:grid-cols-2 lg:col-span-3">
                  {PHOTO_GUIDE.tips.map((tip, i) => (
                    <div key={i} className="bg-offwhite-light p-6 sm:p-7">
                      <p className="flex items-center gap-2.5">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-lime">
                          <Camera className="h-3.5 w-3.5 text-charcoal" aria-hidden="true" />
                        </span>
                        <span className="font-heading text-sm font-bold tracking-tight text-charcoal">
                          {i + 1}. {tip.title}
                        </span>
                      </p>
                      <p className="mt-2.5 text-xs leading-relaxed text-stonebrand">{tip.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-16 rounded-2xl bg-charcoal p-8 text-center sm:p-12" data-testid="faqs-cta">
              <h2 className="text-balance font-heading text-2xl font-bold tracking-tight text-offwhite sm:text-3xl">
                Got a question that isn’t <span className="text-lime">here?</span>
              </h2>
              <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-offwhite/65 sm:text-base">
                Ask it in the enquiry form — there’s no such thing as a daft question, and no obligation attached to
                asking one.
              </p>
              <div className="mt-8">
                <ArrowLink to="/contact" variant="lime" testid="faqs-cta-quote" className="px-8 py-4">
                  Ask Your Question
                </ArrowLink>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
